import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { getSessionDebugInfo, ensureHybridSession } from "@/context/auth/ensureHybridSession";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CheckCircle, RefreshCw, Bug, Copy } from "lucide-react";
import { toast } from "sonner";

interface SessionDebugStripProps {
  ordersCount: number | undefined;
  isLoading: boolean;
  error: Error | null;
}

const SessionDebugStrip = ({ ordersCount, isLoading, error }: SessionDebugStripProps) => {
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const [debugInfo, setDebugInfo] = useState(getSessionDebugInfo());
  const [isRestoring, setIsRestoring] = useState(false);
  const [showDebug, setShowDebug] = useState(false);

  // Refresh debug info periodically
  useEffect(() => {
    const interval = setInterval(() => {
      setDebugInfo(getSessionDebugInfo());
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Determine status
  const hasSessionIssue = isAuthenticated && (!debugInfo.tokenPresent || debugInfo.isExpired);
  const hasNoData = !isLoading && (ordersCount === 0 || ordersCount === undefined) && !error;
  const showWarning = hasSessionIssue || (hasNoData && isAuthenticated);

  const handleRestoreSession = async () => {
    if (!user?.id) {
      toast.error("Geen gebruiker gevonden");
      return;
    }

    setIsRestoring(true);
    try {
      const result = await ensureHybridSession(user.id);
      
      if (result.success) {
        toast.success(result.restored ? "Sessie hersteld!" : "Sessie was al geldig");
        setDebugInfo(getSessionDebugInfo());
        
        // Invalidate queries to retry with new token
        await queryClient.invalidateQueries({ queryKey: ["orders"] });
        await queryClient.invalidateQueries({ queryKey: ["orders-stats"] });
      } else {
        toast.error(`Sessie herstellen mislukt: ${result.error}`);
      }
    } catch (err) {
      toast.error("Onverwachte fout bij herstellen sessie");
    } finally {
      setIsRestoring(false);
    }
  };

  const copyDebugInfo = () => {
    const info = {
      ...debugInfo,
      ordersCount,
      isLoading,
      error: error?.message,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent.slice(0, 100)
    };
    navigator.clipboard.writeText(JSON.stringify(info, null, 2));
    toast.success("Debug info gekopieerd");
  };

  // Only show for management users or when there's an issue
  if (!showWarning && !showDebug) {
    return (
      <button
        onClick={() => setShowDebug(true)}
        className="fixed bottom-2 right-2 p-1 opacity-20 hover:opacity-100 transition-opacity"
        title="Toon debug info"
      >
        <Bug className="h-4 w-4 text-muted-foreground" />
      </button>
    );
  }

  return (
    <div className={`rounded-lg border p-3 mb-4 ${showWarning ? 'bg-destructive/10 border-destructive/30' : 'bg-muted/50 border-border'}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3 text-sm">
          {showWarning ? (
            <AlertTriangle className="h-4 w-4 text-destructive" />
          ) : (
            <CheckCircle className="h-4 w-4 text-primary" />
          )}
          
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={isAuthenticated ? "default" : "destructive"}>
              Auth: {isAuthenticated ? "Ja" : "Nee"}
            </Badge>
            
            <Badge variant={debugInfo.tokenPresent ? "outline" : "destructive"}>
              Token: {debugInfo.tokenPresent ? debugInfo.tokenPreview : "ONTBREEKT"}
            </Badge>
            
            {debugInfo.tokenPresent && (
              <Badge variant={debugInfo.isExpired ? "destructive" : "secondary"}>
                {debugInfo.isExpired ? "VERLOPEN" : `Geldig: ${debugInfo.expiresIn}`}
              </Badge>
            )}
            
            <Badge variant="outline">
              Orders: {isLoading ? "laden..." : ordersCount ?? 0}
            </Badge>
            
            {error && (
              <Badge variant="destructive">
                Error: {error.message.slice(0, 30)}
              </Badge>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {hasSessionIssue && (
            <Button
              size="sm"
              variant="default"
              onClick={handleRestoreSession}
              disabled={isRestoring}
            >
              <RefreshCw className={`h-3 w-3 mr-1 ${isRestoring ? 'animate-spin' : ''}`} />
              {isRestoring ? "Herstellen..." : "Sessie herstellen"}
            </Button>
          )}
          
          <Button
            size="sm"
            variant="ghost"
            onClick={copyDebugInfo}
            title="Kopieer debug info"
          >
            <Copy className="h-3 w-3" />
          </Button>
          
          {showDebug && !showWarning && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowDebug(false)}
            >
              Sluiten
            </Button>
          )}
        </div>
      </div>

      {hasSessionIssue && (
        <p className="text-sm text-warning mt-2">
          Je bent ingelogd, maar je sessie voor data-toegang ontbreekt of is verlopen. 
          Klik op "Sessie herstellen" om dit op te lossen.
        </p>
      )}
    </div>
  );
};

export default SessionDebugStrip;
